import React, { useState, useEffect } from 'react';
import { AlertCircle, Loader2, FileText, Sparkles } from 'lucide-react';
import { fetchAIScripture } from '../lib/openai';

interface BibleSelectorProps {
  onSelect: (passage: { reference: string; text: string; version: string }) => void;
  onClose: () => void;
}

export function BibleSelector({ onSelect, onClose }: BibleSelectorProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [useBlankScripture, setUseBlankScripture] = useState(false);
  const [blankReference, setBlankReference] = useState('');
  const [blankText, setBlankText] = useState('');
  const [useAIScripture, setUseAIScripture] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiTranslation, setAiTranslation] = useState('KJV');

  // Focus input when editing point and set default mode
  useEffect(() => {
    // Default to manual entry mode
    if (!useBlankScripture && !useAIScripture) {
      setUseBlankScripture(true);
    }
    
    if (useBlankScripture) {
      // Focus on blank reference input when switching to blank scripture
      const referenceInput = document.getElementById('blank-reference');
      if (referenceInput) {
        referenceInput.focus();
      }
    } else if (useAIScripture) {
      // Focus on AI prompt input when switching to AI scripture
      const promptInput = document.getElementById('ai-prompt');
      if (promptInput) {
        promptInput.focus();
      }
    }
  }, [useBlankScripture, useAIScripture]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (useBlankScripture) {
      // Handle blank scripture submission
      if (!blankReference.trim()) {
        setError('Please enter a scripture reference');
        return;
      }
      
      onSelect({
        reference: blankReference,
        text: blankText,
        version: 'Manual Entry'
      });
      return;
    }

    if (useAIScripture) {
      // Handle AI scripture submission
      if (!aiPrompt.trim()) {
        setError('Please enter a scripture reference for AI to find');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const passage = await fetchAIScripture(aiPrompt, aiTranslation);
        onSelect(passage);
      } catch (err) {
        console.error('Error fetching AI scripture:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch scripture using AI');
      } finally {
        setLoading(false);
      }
      return;
    }
  };

  

  const toggleScriptureMode = (mode: 'blank' | 'ai') => {
    setUseBlankScripture(mode === 'blank');
    setUseAIScripture(mode === 'ai');
    setError(null);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 min-w-[400px]">
      <h2 className="text-xl font-semibold text-midnight mb-4">Select Bible Passage</h2>
      
      <div className="flex items-center justify-center gap-4 mb-4">
        <button
          onClick={() => toggleScriptureMode('blank')}
          className={`px-4 py-2 text-sm rounded-lg ${
            useBlankScripture || (!useBlankScripture && !useAIScripture)
              ? 'bg-electric text-white' 
              : 'bg-sand/20 text-slate hover:bg-sand/30'
          }`}
        >
          <FileText className="h-4 w-4 inline-block mr-1" />
          Enter Manually
        </button>
        <button
          onClick={() => toggleScriptureMode('ai')}
          className={`px-4 py-2 text-sm rounded-lg ${
            useAIScripture 
              ? 'bg-electric text-white' 
              : 'bg-sand/20 text-slate hover:bg-sand/30'
          }`}
        >
          <Sparkles className="h-4 w-4 inline-block mr-1" />
          AI Insert
        </button>
      </div>
      
      {useAIScripture ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate mb-1">Scripture Reference</label>
            <input
              id="ai-prompt"
              type="text"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent text-midnight"
              placeholder="e.g., John 3:16 or verses about God's love"
            />
            <p className="text-xs text-slate mt-1">Enter a scripture reference or describe the passage you're looking for</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate mb-1">Preferred Translation</label>
            <select
              value={aiTranslation}
              onChange={(e) => {
                setAiTranslation(e.target.value);
              }}
              className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent text-midnight"
            >
              <option value="KJV">King James Version (KJV)</option>
              <option value="NIV">New International Version (NIV)</option>
              <option value="ESV">English Standard Version (ESV)</option>
              <option value="NKJV">New King James Version (NKJV)</option>
              <option value="NASB">New American Standard Bible (NASB)</option>
              <option value="NLT">New Living Translation (NLT)</option>
              <option value="CSB">Christian Standard Bible (CSB)</option>
            </select>
          </div>
          
          {error && (
            <div className="text-sm text-crimson bg-crimson/10 p-3 rounded-lg flex items-start space-x-2">
              <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate hover:text-midnight"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !aiPrompt.trim()}
              className="px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 flex items-center disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Finding Scripture...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Insert Scripture
                </>
              )}
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate mb-1">Scripture Reference</label>
            <input
              id="blank-reference"
              type="text"
              value={blankReference}
              onChange={(e) => setBlankReference(e.target.value)}
              className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent text-midnight"
              placeholder="e.g., John 3:16-17"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate mb-1">Scripture Text</label>
            <textarea
              value={blankText}
              onChange={(e) => setBlankText(e.target.value)}
              rows={6}
              className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent text-midnight"
              placeholder="Enter scripture text here..."
            />
          </div>
          
          {error && (
            <div className="text-sm text-crimson bg-crimson/10 p-3 rounded-lg flex items-start space-x-2">
              <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate hover:text-midnight"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 flex items-center"
            >
              <FileText className="h-4 w-4 mr-2" />
              Insert Scripture
            </button>
          </div>
        </form>
      )}
    </div>
  );
}